import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { hasCode } from '@/api/errors'
import { subscriptionKeys } from '@/features/subscriptions/api/keys'
import { subscriptionService } from '@/features/subscriptions/api/subscriptionService'
import type { Entitlement, PaymentRecord, Plan, PlanDetail } from '@/schemas/subscriptions'

/**
 * The hooks the subscription screens call — one `queryOptions` factory per
 * read (what a route loader hands `ensureRouteData`/`prefetchRouteData`), and
 * the hooks spread it. Nothing here polls.
 *
 * WHERE "CURRENT PLAN + ACTIVE UNTIL" COMES FROM: `subscriptionMeQueryOptions`
 * (`GET subscriptions/me/`), never the chrome's `useMe`. Only it carries
 * `planId` — the one way to reach the plan's period and price, by finding it
 * in `plansQueryOptions` or, for Free and for a retired plan still held, in
 * `planQueryOptions(planId)` — and only its `activeUntil` is already null for
 * Free. `useMe` (`auth/me/`) stays the chrome's: the sidebar chip, TierPill,
 * the Profile hub. Both resolve the same row, so they agree on the tier, and
 * a confirmed payment invalidates both.
 *
 * Two freshness classes:
 *
 * - CATALOGUE — plans and a plan's flags. Authored in an admin: half an hour.
 * - MEMBER — the entitlement and payment history. 30s, so the loader's fetch
 *   is reused on mount and a payment settled by the sweeper in another tab
 *   shows up on the next window focus.
 */

const CATALOG_STALE_MS = 30 * 60_000
const MEMBER_STALE_MS = 30_000

/**
 * The plans `checkout/` sells. Public, so a loader may run it signed out.
 * Group it with `lib/catalog.ts` before drawing it.
 */
export const plansQueryOptions = () =>
  queryOptions<Plan[]>({
    queryKey: subscriptionKeys.plans(),
    queryFn: () => subscriptionService.plans(),
    staleTime: CATALOG_STALE_MS,
  })

export const usePlans = (): UseQueryResult<Plan[]> => useQuery(plansQueryOptions())

export const useSuspensePlans = (): UseSuspenseQueryResult<Plan[]> =>
  useSuspenseQuery(plansQueryOptions())

/**
 * One plan and its feature flags. `null` for `PLAN_NOT_FOUND` — an unknown id,
 * or a retired plan a member may still hold (name it from `planTitle` then).
 */
export const planQueryOptions = (planId: number) =>
  queryOptions<PlanDetail | null>({
    queryKey: subscriptionKeys.plan(planId),
    queryFn: async () => {
      try {
        return await subscriptionService.plan(planId)
      } catch (error) {
        if (hasCode(error, 'PLAN_NOT_FOUND')) return null
        throw error
      }
    },
    staleTime: CATALOG_STALE_MS,
  })

export const usePlan = (planId: number): UseQueryResult<PlanDetail | null> =>
  useQuery(planQueryOptions(planId))

export const useSuspensePlan = (planId: number): UseSuspenseQueryResult<PlanDetail | null> =>
  useSuspenseQuery(planQueryOptions(planId))

/** The member's tier, plan id, `activeUntil`, flags and whether gates are enforced. */
export const subscriptionMeQueryOptions = () =>
  queryOptions<Entitlement>({
    queryKey: subscriptionKeys.me(),
    queryFn: () => subscriptionService.me(),
    staleTime: MEMBER_STALE_MS,
  })

export const useSubscriptionMe = (): UseQueryResult<Entitlement> =>
  useQuery(subscriptionMeQueryOptions())

export const useSuspenseSubscriptionMe = (): UseSuspenseQueryResult<Entitlement> =>
  useSuspenseQuery(subscriptionMeQueryOptions())

/** The member's attempts, newest first — abandoned Pending rows included. */
export const paymentsQueryOptions = () =>
  queryOptions<PaymentRecord[]>({
    queryKey: subscriptionKeys.payments(),
    queryFn: () => subscriptionService.payments(),
    staleTime: MEMBER_STALE_MS,
  })

export const useSuspensePayments = (): UseSuspenseQueryResult<PaymentRecord[]> =>
  useSuspenseQuery(paymentsQueryOptions())
